import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge, Form, Table } from 'react-bootstrap';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ROUTES } from '../constants';
import './ProjectList.css';

const STATUS_VARIANTS = {
  'in progress': 'primary',
  'on hold': 'warning',
  completed: 'success',
  cancelled: 'secondary',
};

const STATUS_MARKER_COLORS = {
  'in progress': '#95b7d8',
  'on hold': '#d9d9d9',
  completed: '#198754',
  cancelled: '#6c757d',
};

const DEFAULT_MARKER_COLOR = '#1B467E';

const getStatusVariant = (status) =>
  STATUS_VARIANTS[String(status || '').toLowerCase()] || 'secondary';

const getStatusMarkerColor = (status) =>
  STATUS_MARKER_COLORS[String(status || '').toLowerCase()] || DEFAULT_MARKER_COLOR;

const buildStatusMarkerIcon = (status) => {
  const color = getStatusMarkerColor(status);
  const html = `
    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41" aria-hidden="true">
      <path d="M12.5 0C5.6 0 0 5.6 0 12.5c0 9.4 12.5 28.5 12.5 28.5S25 21.9 25 12.5C25 5.6 19.4 0 12.5 0z" fill="${color}" stroke="#212529" stroke-width="1"/>
      <circle cx="12.5" cy="12.5" r="4.5" fill="#fff" stroke="#212529" stroke-width="0.75"/>
    </svg>
  `;
  return L.divIcon({
    html,
    className: 'project-map-marker',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
  });
};

const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (char) => {
    switch (char) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });

const ProjectList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [countyFilter, setCountyFilter] = useState('');
  const [projectDetails, setProjectDetails] = useState({});
  const navigate = useNavigate();

  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerLayerRef = useRef(null);

  useEffect(() => {
    const adlsEndpoint = process.env.REACT_APP_ADLS_ENDPOINT;
    if (!adlsEndpoint) {
      return;
    }

    let isMounted = true;

    Promise.all(
      ROUTES.map((project) =>
        fetch(`${adlsEndpoint}${project.endpoint}`)
          .then((response) => (response.ok ? response.json() : null))
          .then((detail) => ({ path: project.path, detail }))
          .catch(() => ({ path: project.path, detail: null }))
      )
    ).then((results) => {
      if (!isMounted) {
        return;
      }

      const detailsByPath = {};
      results.forEach(({ path, detail }) => {
        detailsByPath[path] = detail;
      });
      setProjectDetails(detailsByPath);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  // Initialize the overview map once.
  useEffect(() => {
    if (!mapContainerRef.current || mapInstanceRef.current) {
      return undefined;
    }

    const map = L.map(mapContainerRef.current, {
      scrollWheelZoom: false,
      maxBoundsViscosity: 1.0,
    }).setView([40.9, -77.5], 7); // centered on Pennsylvania

    L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
      {
        maxZoom: 18,
        attribution: 'Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ',
      }
    ).addTo(map);

    markerLayerRef.current = L.layerGroup().addTo(map);
    mapInstanceRef.current = map;

    return () => {
      map.remove();
      mapInstanceRef.current = null;
      markerLayerRef.current = null;
    };
  }, []);

  // Refresh markers whenever project details (and thus coordinates) change.
  useEffect(() => {
    const map = mapInstanceRef.current;
    const layer = markerLayerRef.current;
    if (!map || !layer) {
      return;
    }

    layer.clearLayers();
    const points = [];

    ROUTES.forEach((project) => {
      const detail = projectDetails[project.path];
      const latitude = Number.parseFloat(detail?.latitude);
      const longitude = Number.parseFloat(detail?.longitude);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return;
      }

      const popupNode = document.createElement('div');
      popupNode.className = 'project-map-popup';
      popupNode.innerHTML = `
        <strong>${escapeHtml(project.name)}</strong>
        <div class="project-map-popup-meta"><em>Status:</em> ${escapeHtml(project.status)}</div>
        <div class="project-map-popup-meta"><em>Sponsor:</em> ${escapeHtml(project.sponsor)}</div>
      `;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'project-map-popup-link';
      button.textContent = 'View project →';
      button.addEventListener('click', () => navigate(project.path));
      popupNode.appendChild(button);

      const marker = L.marker([latitude, longitude], {
        icon: buildStatusMarkerIcon(project.status),
      });
      marker.bindPopup(popupNode);
      marker.addTo(layer);
      points.push([latitude, longitude]);
    });

    if (points.length === 1) {
      map.setView(points[0], 10);
      const bounds = L.latLng(points[0]).toBounds(50000); // ~50km around the marker
      map.setMaxBounds(bounds.pad(0.25));
    } else if (points.length > 1) {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 10 });
      map.setMaxBounds(bounds.pad(0.25));
    } else {
      map.setMaxBounds(null);
    }
  }, [projectDetails, navigate]);

  const metaByPath = useMemo(() => {
    const result = {};
    ROUTES.forEach((project) => {
      const detail = projectDetails[project.path];
      result[project.path] = {
        type: String(detail?.type ?? '').trim(),
        county: String(detail?.county ?? '').trim(),
      };
    });
    return result;
  }, [projectDetails]);

  const statusOptions = useMemo(() => {
    const values = new Set();
    ROUTES.forEach((project) => {
      const status = String(project.status ?? '').trim();
      if (status) {
        values.add(status);
      }
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, []);

  const typeOptions = useMemo(() => {
    const values = new Set();
    Object.values(metaByPath).forEach(({ type }) => {
      if (type) {
        values.add(type);
      }
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [metaByPath]);

  const countyOptions = useMemo(() => {
    const values = new Set();
    Object.values(metaByPath).forEach(({ county }) => {
      if (county) {
        values.add(county);
      }
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [metaByPath]);

  const filteredProjects = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return ROUTES.filter((project) => {
      const { type, county } = metaByPath[project.path] || {};

      if (statusFilter && project.status !== statusFilter) {
        return false;
      }
      if (typeFilter && type !== typeFilter) {
        return false;
      }
      if (countyFilter && county !== countyFilter) {
        return false;
      }
      if (!query) {
        return true;
      }
      return [project.name, project.sponsor]
        .join(' ')
        .toLowerCase()
        .includes(query);
    });
  }, [searchTerm, statusFilter, typeFilter, countyFilter, metaByPath]);

  return (
    <div className="project-list-container">
      <div
        ref={mapContainerRef}
        className="projects-overview-map"
        role="region"
        aria-label="Map of all Fast Track projects"
      />

      <div className="project-filters">
        <Form.Group className="project-search-group" controlId="projectSearch">
          <Form.Label>Search projects</Form.Label>
          <Form.Control
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search by project or sponsor"
          />
        </Form.Group>

        <Form.Group className="project-filter-group" controlId="projectTypeFilter">
          <Form.Label>Project Type</Form.Label>
          <Form.Select
            value={typeFilter}
            onChange={(event) => setTypeFilter(event.target.value)}
          >
            <option value="">All types</option>
            {typeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="project-filter-group" controlId="projectCountyFilter">
          <Form.Label>County</Form.Label>
          <Form.Select
            value={countyFilter}
            onChange={(event) => setCountyFilter(event.target.value)}
          >
            <option value="">All counties</option>
            {countyOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="project-filter-group" controlId="projectStatusFilter">
          <Form.Label>Status</Form.Label>
          <Form.Select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="">All statuses</option>
            {statusOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </div>

      {filteredProjects.length > 0 ? (
        <Table responsive striped className="project-table align-middle">
          <thead>
            <tr>
              <th scope="col">Project</th>
              <th scope="col">Sponsor</th>
              <th scope="col">Project Type</th>
              <th scope="col">County</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredProjects.map((project) => {
              const { type, county } = metaByPath[project.path] || {};
              return (
                <tr key={project.path}>
                  <td className="project-table-name-cell">
                    <Link to={project.path} className="project-table-link">
                      {project.name}
                    </Link>
                  </td>
                  <td className="project-table-sponsor-cell">{project.sponsor}</td>
                  <td className="project-table-type-cell">
                    {type ? (
                      type
                    ) : (
                      <span className="project-table-meta-empty">—</span>
                    )}
                  </td>
                  <td className="project-table-county-cell">
                    {county ? (
                      county
                    ) : (
                      <span className="project-table-meta-empty">—</span>
                    )}
                  </td>
                  <td>
                    <Badge
                      bg={getStatusVariant(project.status)}
                      className={`project-status-badge project-status-badge-${String(
                        project.status || ''
                      )
                        .toLowerCase()
                        .replace(/\s+/g, '-')}`}
                    >
                      {project.status}
                    </Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      ) : (
        <p className="project-no-results">No projects match your search.</p>
      )}
    </div>
  );
};

export default ProjectList;
